<script lang="ts">
  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import type { FriendEntry, BlockEntry } from '../../../shared/types';

  /**
   * Friends + blocks panel, mounted inside Profile. Three lists:
   *   - Outgoing: people I've added (my friends-only audience)
   *   - Incoming: people who've added me (I see their friends-only content)
   *   - Blocked: people I've blocked (mutual block on friends-only flow)
   *
   * Friendship is directional. "A added B" doesn't imply "B added A".
   * Blocks are also directional but the server filter applies them
   * symmetrically when evaluating friends-only visibility, so blocking is
   * effectively bidirectional in practice.
   */

  let outgoing: FriendEntry[] = $state([]);
  let incoming: FriendEntry[] = $state([]);
  let blocked: BlockEntry[] = $state([]);
  let loadError: string | null = $state(null);
  let loading = $state(true);

  // Add-friend form
  let addUsername = $state('');
  let addBusy = $state(false);
  let addError: string | null = $state(null);

  onMount(load);

  async function load() {
    loading = true;
    loadError = null;
    try {
      [outgoing, incoming, blocked] = await Promise.all([
        api.listFriends(),
        api.listIncomingFriends(),
        api.listBlocks(),
      ]);
    } catch {
      loadError = 'Kunne ikke laste vennelisten.';
    } finally {
      loading = false;
    }
  }

  function displayName(e: { display_name: string | null; username: string | null }): string {
    return (e.display_name && e.display_name.trim())
      || e.username
      || '(ukjent)';
  }

  async function addFriend(e: SubmitEvent) {
    e.preventDefault();
    addError = null;
    const name = addUsername.trim().toLowerCase();
    if (!name) {
      addError = 'Skriv inn et brukernavn.';
      return;
    }
    addBusy = true;
    try {
      const added = await api.addFriend({ username: name });
      // Dedupe: if the friend was already there, the server returned the same
      // row; replace by user_id.
      outgoing = [added, ...outgoing.filter((f) => f.user_id !== added.user_id)];
      addUsername = '';
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        addError = 'Fant ingen bruker med det brukernavnet. (Brukeren må ha satt opp brukernavn for å være søkbar.)';
      } else if (err instanceof ApiError && err.status === 400) {
        addError = 'Du kan ikke legge til deg selv.';
      } else {
        addError = 'Klarte ikke å legge til venn.';
      }
    } finally {
      addBusy = false;
    }
  }

  async function removeFriend(f: FriendEntry) {
    if (!confirm(`Fjerne ${displayName(f)} fra vennelisten din?`)) return;
    try {
      await api.removeFriend(f.user_id);
      outgoing = outgoing.filter((x) => x.user_id !== f.user_id);
    } catch {
      loadError = 'Klarte ikke å fjerne venn.';
    }
  }

  // "Legg til som venn tilbake" — for an incoming entry where I haven't
  // already added them. Goes through the same addFriend API as the form
  // above. If the user hasn't set a username (rare — incoming implies they
  // added someone, which requires THE OTHER PARTY's username, not theirs)
  // we hide the button instead of erroring.
  async function addBack(f: FriendEntry) {
    if (!f.username) return;
    try {
      const added = await api.addFriend({ username: f.username });
      outgoing = [added, ...outgoing.filter((o) => o.user_id !== added.user_id)];
    } catch {
      loadError = 'Klarte ikke å legge til venn.';
    }
  }

  async function block(e: FriendEntry) {
    if (!confirm(`Blokkere ${displayName(e)}? De vil ikke lenger se aktiviteter du deler med venner.`)) return;
    try {
      const blockedRow = await api.blockUser(e.user_id);
      blocked = [blockedRow, ...blocked.filter((b) => b.user_id !== blockedRow.user_id)];
    } catch {
      loadError = 'Klarte ikke å blokkere.';
    }
  }

  async function unblock(b: BlockEntry) {
    try {
      await api.unblockUser(b.user_id);
      blocked = blocked.filter((x) => x.user_id !== b.user_id);
    } catch {
      loadError = 'Klarte ikke å oppheve blokkering.';
    }
  }
</script>

<section class="card" aria-labelledby="friends-h">
  <h3 id="friends-h">Venner</h3>
  <p class="muted">
    Vennskap er enveis. Du kan se aktiviteter merket «Venner» fra noen som
    har lagt deg til, og motsatt. Brukere må ha satt et brukernavn for å
    være søkbare.
  </p>

  {#if loadError}<p class="error" role="alert">{loadError}</p>{/if}

  <form onsubmit={addFriend} style="margin-bottom: 1rem;">
    <label for="add-friend-u">Legg til ved brukernavn</label>
    <div class="row" style="align-items: stretch;">
      <input id="add-friend-u" type="text" bind:value={addUsername}
             placeholder="brukernavn" pattern="[a-z0-9_-]*"
             autocomplete="off" style="flex: 1;" />
      <button class="primary" type="submit" disabled={addBusy}>
        {addBusy ? 'Legger til …' : 'Legg til'}
      </button>
    </div>
    {#if addError}<p class="error" role="alert" style="margin-top: 0.5rem;">{addError}</p>{/if}
  </form>

  {#if loading}
    <p class="muted">Laster …</p>
  {:else}
    <h4 style="margin-bottom: 0.25rem;">Vennene mine ({outgoing.length})</h4>
    {#if outgoing.length === 0}
      <p class="muted">Du har ikke lagt til noen ennå.</p>
    {/if}
    {#each outgoing as f (f.user_id)}
      <article class="card" style="margin: 0.4rem 0; padding: 0.5rem 0.75rem;">
        <div class="row" style="justify-content: space-between;">
          <span>
            {displayName(f)}
            {#if f.username}<span class="muted">· @{f.username}</span>{/if}
          </span>
          <button class="danger" type="button" onclick={() => removeFriend(f)}>Fjern</button>
        </div>
      </article>
    {/each}

    <h4 style="margin: 1rem 0 0.25rem;">Har lagt meg til ({incoming.length})</h4>
    {#if incoming.length === 0}
      <p class="muted">Ingen har lagt deg til ennå.</p>
    {/if}
    {#each incoming as f (f.user_id)}
      {@const alreadyFriend = outgoing.some((o) => o.user_id === f.user_id)}
      <article class="card" style="margin: 0.4rem 0; padding: 0.5rem 0.75rem;">
        <div class="row" style="justify-content: space-between; gap: 0.5rem;">
          <span>
            {displayName(f)}
            {#if f.username}<span class="muted">· @{f.username}</span>{/if}
            {#if alreadyFriend}<span class="muted" style="font-size: 0.85rem;">(gjensidig)</span>{/if}
          </span>
          <div class="row" style="gap: 0.4rem;">
            {#if !alreadyFriend && f.username}
              <button class="primary" type="button" onclick={() => addBack(f)}>
                Legg til som venn
              </button>
            {/if}
            <button class="danger" type="button" onclick={() => block(f)}>Blokker</button>
          </div>
        </div>
      </article>
    {/each}

    {#if blocked.length}
      <h4 style="margin: 1rem 0 0.25rem;">Blokkerte ({blocked.length})</h4>
      {#each blocked as b (b.user_id)}
        <article class="card" style="margin: 0.4rem 0; padding: 0.5rem 0.75rem;">
          <div class="row" style="justify-content: space-between;">
            <span>
              {displayName(b)}
              {#if b.username}<span class="muted">· @{b.username}</span>{/if}
            </span>
            <button type="button" onclick={() => unblock(b)}>Opphev</button>
          </div>
        </article>
      {/each}
    {/if}
  {/if}
</section>
